import * as THREE from 'three';
import { MASK } from '../world/Physics.js';
import { EnemyModel, HITBOX_DEFS } from './EnemyModel.js';
import { TEAM, MEANS } from '../game/MatchAuthority.js';

/** Used when there is no director: every multiplier neutral. */
const NEUTRAL_SKILL = { aim: 1, reaction: 1, awareness: 1, sight: 1 };

/**
 * A single hostile soldier: perception, behaviour, movement and gunplay.
 *
 * The design goal is that a firefight should feel like fighting *soldiers*, which
 * means the AI has to be legibly imperfect:
 *
 * - It does not know where you are until it *sees* you, and even then there is a
 *   reaction delay and an awareness ramp before it can shoot. Instant omniscient
 *   fire is the single fastest way to make a shooter feel cheap.
 * - Its aim carries a wandering error that converges the longer it holds you in
 *   its sights, so trading shots at range is survivable and peeking out of cover
 *   repeatedly is not.
 * - It fires in bursts with human gaps, ducks when suppressed, reloads behind
 *   cover, and defers to the director for whether it is the one pushing.
 *
 * The body is an `EnemyModel`; hitboxes are re-derived from the animated bones
 * every frame so that what you shoot at is what you hit.
 */

export const TUNING = {
  health: 100,
  radius: 0.36,
  standHeight: 1.8,
  crouchHeight: 1.2,
  eyeHeight: 1.62,
  crouchEyeHeight: 1.05,

  walkSpeed: 2.3,
  combatSpeed: 3.6,
  sprintSpeed: 5.5,
  crouchSpeed: 1.5,
  accel: 26,
  friction: 11,
  gravity: 21.5,
  turnRate: 7.5,

  // --- perception ---
  fov: 110 * (Math.PI / 180),
  sightRange: 62,
  /**
   * Metres of bias toward the target already being tracked, applied when picking
   * who to look at. Without it two hostiles at similar range make a soldier swap
   * targets every perception tick and never finish an aim.
   */
  targetStickiness: 8,
  /** Metres a squad scatters around an objective rally point rather than stacking on it. */
  rallySpread: 12,
  peripheralRange: 14, // close targets are noticed even off-centre
  awarenessGain: 2.6, // per second at ideal conditions
  awarenessDecay: 0.42,
  reactionMin: 0.25,
  reactionMax: 0.5,
  memoryTime: 7.0, // how long a lost target stays "known"
  hearingRange: 42,

  // --- gunplay ---
  magSize: 30,
  fireInterval: 0.105,
  reloadTime: 2.55,
  damage: 13,
  burstMin: 3,
  burstMax: 6,
  burstGapMin: 0.42,
  burstGapMax: 1.15,
  suppressBurstGap: 0.8,
  range: 90,

  aimSettle: 1.5, // seconds of sustained aim to reach best accuracy
  aimErrorNear: 0.9, // degrees at best, close range
  aimErrorFar: 2.4, // degrees at best, long range
  aimErrorCold: 5.5, // extra degrees before settling
  aimErrorMoving: 2.2,
  aimErrorSuppressed: 4.5,
  aimTurnRate: 6.5,
  perShotSpread: 0.55,

  // --- cover / tactics ---
  coverSearchRadius: 22,
  peekTimeMin: 1.1,
  peekTimeMax: 2.6,
  duckTimeMin: 0.8,
  duckTimeMax: 2.0,
  suppressionDecay: 0.75,
  suppressionThreshold: 1.0,
  nearMissRadius: 1.6,

  repathInterval: 0.9,
  repathMoveThreshold: 2.4,
  perceptionInterval: 0.1,
};

export const STATE = {
  IDLE: 'IDLE',
  ALERT: 'ALERT',
  SEARCH: 'SEARCH',
  COMBAT: 'COMBAT',
  SUPPRESSED: 'SUPPRESSED',
  RELOAD: 'RELOAD',
  FLANK: 'FLANK',
  DEAD: 'DEAD',
};

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _d = new THREE.Vector3();
const _e = new THREE.Vector3();
const _delta = new THREE.Vector3();
const DOWN = new THREE.Vector3(0, -1, 0);
const clamp = THREE.MathUtils.clamp;
const DEG = Math.PI / 180;

let _seq = 0;

export class Enemy {
  constructor(ctx, director, spawn, variant = 0, team = TEAM.BRAVO) {
    this.ctx = ctx;
    this.director = director;
    this.physics = ctx.physics;
    this.events = ctx.events;
    this.rng = ctx.rng ?? Math.random;

    this.id = `enemy_${_seq++}`;
    this.index = variant;

    this.position = spawn.clone();
    this.velocity = new THREE.Vector3();
    this.yaw = this.rng() * Math.PI * 2;
    this.grounded = false;
    this.height = TUNING.standHeight;
    this.crouching = false;
    this.crouchAmount = 0;

    this.health = TUNING.health;
    this.dead = false;
    this.corpseTime = 0;

    /* ---- perception ---- */
    this.awareness = 0;
    this.canSee = false;
    this.target = null;
    this.lastKnown = new THREE.Vector3();
    this.lastKnownAge = 99;
    this.reactionTimer = 0;
    this.hasEngaged = false;
    this.suspicion = new THREE.Vector3();
    this.hasSuspicion = false;
    this._percTimer = this.rng() * TUNING.perceptionInterval;
    this._distToTarget = 999;

    /* ---- weapon ---- */
    this.mag = TUNING.magSize;
    this.fireTimer = 0;
    this.burstLeft = 0;
    this.burstGap = 0;
    this.reloadTimer = 0;
    this.aimDir = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.aimTime = 0;
    this.aimErrPhase = this.rng() * 100;
    this.suppression = 0;

    /* ---- behaviour ---- */
    this.state = STATE.IDLE;
    this.stateTime = 0;
    this.role = 'hold'; // hold | push | suppress | flank
    this.cover = null;
    this.peeking = true;
    this.peekTimer = 1;
    this.idleTimer = 2 + this.rng() * 4;

    this.moveTarget = null;
    this.desiredSpeed = TUNING.walkSpeed;

    /** Tactical-equipment timers. See applyStatus. */
    this.status = { blindT: 0, slowT: 0, slowMul: 1, inhibitT: 0 };

    this.flankPoint = null;
    this.path = [];
    this.pathIndex = 0;
    this.repathTimer = 0;
    this.pathTarget = new THREE.Vector3();
    this.pathPending = false;
    this.stuckTimer = 0;
    this._lastPos = spawn.clone();
    this._avoidX = 0;
    this._avoidZ = 0;
    this._steerOut = new THREE.Vector3();
    this._wish = new THREE.Vector3();
    this._footMat = 'concrete';

    /* ---- model ---- */
    this.model = new EnemyModel(ctx, variant, team);
    this.model.yaw = this.yaw;
    ctx.scene.add(this.model.root);

    /* ---- physics actor ---- */
    this.boundingRadius = 1.95;
    this.matrixWorld = new THREE.Matrix4();
    this.hitboxes = HITBOX_DEFS.map((d) => ({
      offset: new THREE.Vector3(d.offset[0], d.offset[1], d.offset[2]),
      halfExtents: new THREE.Vector3(d.half[0], d.half[1], d.half[2]),
      tag: d.tag,
      damageScale: d.damageScale,
      bone: this.model.bone[d.bone],
      boneName: d.bone,
      worldMatrix: new THREE.Matrix4(),
    }));
    this.physics.registerActor(this);
    /**
     * The side, decided by whoever spawned them rather than by being an `Enemy`.
     * The class name is now a lie in team modes — these are soldiers, and half of
     * them are on yours — but renaming it would touch forty files for no behaviour,
     * so the honesty lives in this field instead.
     */
    this.team = team;
    ctx.match?.register(this, {
      team,
      kind: 'bot',
      bot: true,
      name: team === TEAM.BRAVO ? 'HOSTILE' : 'ALLY',
    });

    this._snapToGround();
    this._syncHitboxes();
  }

  /* ------------------------------------------------------------------ *
   * Frame update                                                        *
   * ------------------------------------------------------------------ */

  update(dt, elapsed) {
    if (this.dead) {
      this.corpseTime += dt;
      this.model.update(dt, null);
      return;
    }

    this.stateTime += dt;
    this.suppression = Math.max(0, this.suppression - TUNING.suppressionDecay * dt);
    this.lastKnownAge += dt;
    if (this.reactionTimer > 0) this.reactionTimer -= dt;

    this._percTimer -= dt;
    if (this._percTimer <= 0) {
      this._percTimer = TUNING.perceptionInterval;
      this._perceive(TUNING.perceptionInterval);
    }

    this._tickStatus(dt);
    this._think(dt);
    this._updateAim(dt);
    this._updateWeapon(dt);
    this._move(dt);
    this._updateModel(dt);
    this._syncHitboxes();
    void elapsed;
  }

  /* ------------------------------------------------------------------ *
   * Perception                                                          *
   * ------------------------------------------------------------------ */

  /**
   * The two properties another soldier's perception reads off a target.
   *
   * Perception was written against the player and reads `target.eye` and
   * `target.speed`; giving a soldier the same two makes one soldier a valid target
   * for another without perception needing to know which kind of thing it is
   * looking at. That is the whole adapter — the shapes were one rename apart.
   */
  get eye() {
    return this.crouching ? TUNING.crouchEyeHeight : TUNING.eyeHeight;
  }

  /**
   * The four bot-skill multipliers, or a neutral set.
   *
   * Read through a getter rather than copied at construction so changing the
   * setting takes hold on soldiers already on the map — and so there is exactly
   * one place that decides what "no director" means.
   */
  get skill() {
    return this.director?.skill ?? NEUTRAL_SKILL;
  }

  /** How far this soldier can see, after skill. */
  get sightRange() {
    return TUNING.sightRange * this.skill.sight;
  }

  get speed() {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  get eyePosition() {
    return _a.set(
      this.position.x,
      this.position.y + (this.crouching ? TUNING.crouchEyeHeight : TUNING.eyeHeight),
      this.position.z
    );
  }

  /**
   * The nearest living hostile worth looking at, or null.
   *
   * Perception used to open with `const player = this.ctx.player` and never
   * reconsider: there was one thing in the world to shoot at, so "who" was not a
   * question. With two teams of soldiers on the map it is the *first* question, and
   * everything downstream — the sight cone, the awareness ramp, the last-known
   * position — works on whoever this returns.
   *
   * Chosen by distance, not by threat. A soldier looks at what is in front of them;
   * anything cleverer than that reads as the AI knowing where the danger is before
   * it can see it. The current target keeps a bias so a firefight does not flicker
   * between two enemies standing a metre apart.
   */
  _acquire() {
    const match = this.ctx.match;
    if (!match) {
      const p = this.ctx.player;
      return p && !p.dead ? p : null;
    }
    let best = null;
    /**
     * Deliberately not capped at `sightRange`.
     *
     * The sight test below is what decides whether they can actually see this
     * person; this only decides who they are trying to see. Capping it here looked
     * right and was measurably wrong: bots spawn 26–85 m out, so on a 62 m cap most
     * of the squad acquired nobody, took the "no hostiles left" branch, and decayed
     * awareness three times as fast while dropping the target they were hunting.
     * Selecting a distant hostile and failing to see them is exactly what the old
     * code did with the player, every frame.
     */
    let bestD = Infinity;
    for (const reg of match.actors.values()) {
      if (reg.dead || reg.actor === this) continue;
      const a = reg.actor;
      if (!a.position || typeof a.health !== 'number') continue;
      if (!match.hostile(this, a)) continue;
      let d = this.position.distanceTo(a.position);
      // Stickiness, in metres. Cheaper and more legible than a hysteresis timer.
      if (a === this.target) d -= TUNING.targetStickiness;
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    return best;
  }

  _perceive(dt) {
    // Flashed: the eyes report nothing. `canSee` is cleared rather than the whole
    // function skipped, so awareness still decays and they do not stand frozen
    // holding a target they cannot possibly still be tracking.
    if (this.status.blindT > 0) {
      this.canSee = false;
      this.awareness = Math.max(0, this.awareness - TUNING.awarenessDecay * dt);
      return;
    }
    const player = this._acquire();
    if (!player || player.dead) {
      this.canSee = false;
      this.target = null;
      this.awareness = Math.max(0, this.awareness - TUNING.awarenessDecay * dt * 3);
      return;
    }

    const eye = _a.set(
      this.position.x,
      this.position.y + (this.crouching ? TUNING.crouchEyeHeight : TUNING.eyeHeight),
      this.position.z
    );
    const chest = _b.set(player.position.x, player.position.y + 0.95, player.position.z);
    const to = _c.subVectors(chest, eye);
    const dist = to.length();
    this._distToTarget = dist;

    let see = false;
    if (dist < this.sightRange) {
      to.multiplyScalar(1 / dist);
      const fwd = _d.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      const cosAng = fwd.x * to.x + fwd.z * to.z;
      const inCone = cosAng > Math.cos(TUNING.fov * 0.5);
      const close = dist < TUNING.peripheralRange;
      if (inCone || close) {
        // Try the chest first, then the head: partial cover should not grant
        // total invisibility, but a properly covered player stays hidden.
        see = this.physics.visible(eye, chest, this);
        if (!see) {
          _e.set(player.position.x, player.position.y + player.eye * 0.98, player.position.z);
          see = this.physics.visible(eye, _e, this);
        }
      }
    }

    this.canSee = see;
    if (see) {
      // Awareness ramps: fast for a sprinting target in the open at close range,
      // slow for a crouched target at 50 m in the corner of the eye.
      const fwd = _d.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      const centred = clamp(fwd.x * to.x + fwd.z * to.z, 0, 1);
      const rangeFactor = clamp(1 - dist / this.sightRange, 0.12, 1);
      const motion = clamp(player.speed / 6, 0, 1);
      const stanceFactor = player.crouching ? 0.55 : 1;
      const gain =
        TUNING.awarenessGain * this.skill.awareness *
        (0.35 + centred * 0.65) *
        (0.35 + rangeFactor * 0.9) *
        (0.55 + motion * 0.7) *
        stanceFactor;
      this.awareness = Math.min(1, this.awareness + gain * dt);

      this.lastKnown.copy(player.position);
      this.lastKnownAge = 0;
      if (this.awareness >= 1) {
        if (!this.target) {
          // First acquisition: a human needs a beat before the trigger pull.
          this.reactionTimer =
            (TUNING.reactionMin + this.rng() * (TUNING.reactionMax - TUNING.reactionMin)) *
            this.skill.reaction;
          this.director?.onSpotted(this);
          // The shout is the player's cue that they have been seen; it lands
          // during the reaction delay, so it is a warning, not an obituary.
          this.events.emit('audio:play', {
            id: 'enemy_alert',
            position: this.position.clone(),
            volume: 0.95,
            pitch: 0.93 + this.rng() * 0.16,
          });
        }
        this.target = player;
      }
    } else {
      this.awareness = Math.max(0, this.awareness - TUNING.awarenessDecay * dt);
      if (this.lastKnownAge > TUNING.memoryTime) this.target = null;
    }
  }

  /** Heard something at `pos`. `loud` scales how precisely it is localised. */
  onSound(pos, loud = 1) {
    if (this.dead) return;
    const d = this.position.distanceTo(pos);
    const range = TUNING.hearingRange * loud;
    if (d > range) return;
    const falloff = 1 - d / range;
    this.awareness = Math.min(1, this.awareness + 0.35 * falloff + 0.1);
    // Localisation degrades with distance, so distant gunfire sends them to the
    // right area rather than to your exact feet.
    const err = (1 - falloff) * 6;
    this.suspicion.set(
      pos.x + (this.rng() - 0.5) * err,
      pos.y,
      pos.z + (this.rng() - 0.5) * err
    );
    this.hasSuspicion = true;
    if (!this.target && this.state === STATE.IDLE) this._setState(STATE.ALERT);
    else if (!this.target && this.state === STATE.ALERT && this.stateTime > 0.8) this._setState(STATE.SEARCH);
  }

  /** A shot passed nearby: suppression plus an instant idea of where it came from. */
  onNearMiss(origin, dir, strength = 1) {
    if (this.dead) return;
    this.suppression += strength;
    this.awareness = Math.min(1, this.awareness + 0.5);
    if (!this.target) {
      this.suspicion.copy(origin);
      this.hasSuspicion = true;
      if (this.state === STATE.IDLE || this.state === STATE.ALERT) this._setState(STATE.SEARCH);
    }
  }

  /* ------------------------------------------------------------------ *
   * Behaviour state machine                                             *
   * ------------------------------------------------------------------ */

  _setState(s) {
    if (this.state === s) return;
    this.state = s;
    this.stateTime = 0;
  }

  _think(dt) {
    const engaged = !!this.target && this.lastKnownAge < TUNING.memoryTime;

    // Reload pre-empts everything except death.
    if (this.state === STATE.RELOAD) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        this.mag = TUNING.magSize;
        this._setState(engaged ? STATE.COMBAT : STATE.SEARCH);
      } else {
        this._reloadBehaviour();
        return;
      }
    }

    if (this.suppression > TUNING.suppressionThreshold && this.state !== STATE.SUPPRESSED && engaged) {
      this._setState(STATE.SUPPRESSED);
    }

    switch (this.state) {
      case STATE.IDLE:
        this._idle(dt);
        break;
      case STATE.ALERT:
        this._alert(dt);
        break;
      case STATE.SEARCH:
        this._search(dt);
        break;
      case STATE.COMBAT:
        this._combat(dt);
        break;
      case STATE.SUPPRESSED:
        this._suppressed(dt);
        break;
      case STATE.FLANK:
        this._flank(dt);
        break;
      default:
        break;
    }

    if (engaged && this.state !== STATE.COMBAT && this.state !== STATE.SUPPRESSED && this.state !== STATE.FLANK) {
      this._setState(STATE.COMBAT);
    }
  }

  _idle(dt) {
    this.crouching = false;
    this.idleTimer -= dt;
    if (this.awareness > 0.25) {
      this._setState(STATE.ALERT);
      return;
    }
    if (this.idleTimer <= 0) {
      this.idleTimer = 5 + this.rng() * 9;
      /**
       * A mode may have somewhere it wants this side to be — a bomb site to take
       * or to hold. Without this hook a soldier with no one to shoot wanders at
       * random, which is fine when the only thing on the map is a firefight and
       * useless in a mode where standing in the right place *is* the win
       * condition: attackers would never reach a site and every round would end
       * on the clock.
       *
       * Deliberately only in IDLE. A soldier who can see someone keeps fighting;
       * objectives redirect the walk, they do not override the war.
       */
      const rally = this.director?.rallyFor?.(this);
      const p = rally
        ? this.director.nav?.randomWalkable(this.rng, rally, 0, TUNING.rallySpread)
        : this.director?.nav?.randomWalkable(this.rng, this.position, 4, 16);
      if (p) this._goTo(p, TUNING.walkSpeed);
      else this.moveTarget = null;
    }
    if (!this.moveTarget) this._faceIdle(dt);
  }

  _alert(dt) {
    this.crouching = false;
    if (this.target) {
      this._setState(STATE.COMBAT);
      return;
    }
    // Turn toward whatever caught the attention, weapon up.
    if (this.hasSuspicion) this._faceTowards(this.suspicion, dt, 3.2);
    if (this.stateTime > 1.1 + this.rng() * 0.6) {
      this._setState(STATE.SEARCH);
    }
    if (this.awareness < 0.06 && this.stateTime > 3) this._setState(STATE.IDLE);
  }

  _search(dt) {
    this.crouching = false;
    const goal = this.lastKnownAge < TUNING.memoryTime ? this.lastKnown : this.hasSuspicion ? this.suspicion : null;
    if (!goal) {
      this._setState(STATE.IDLE);
      return;
    }
    const d = this.position.distanceTo(goal);
    if (d > 2.2) {
      this._goTo(goal, TUNING.combatSpeed * 0.85);
    } else {
      // Arrived. Sweep the area, then pick a new spot to check.
      this.moveTarget = null;
      this.yaw += Math.sin(this.stateTime * 1.4) * dt * 1.6;
      if (this.stateTime > 4 + this.rng() * 3) {
        const p = this.director?.nav?.randomWalkable(this.rng, goal, 3, 12);
        if (p) {
          this.hasSuspicion = true;
          this.suspicion.copy(p);
          this.stateTime = 0;
        } else {
          this._setState(STATE.IDLE);
        }
      }
    }
    if (this.stateTime > 16) this._setState(STATE.IDLE);
  }

  _combat(dt) {
    const player = this.target;
    if (!player) {
      this._setState(STATE.SEARCH);
      return;
    }
    this.hasEngaged = true;

    if (this.role === 'flank' && this.lastKnownAge < 2) {
      this._setState(STATE.FLANK);
      return;
    }

    // --- cover management ---
    const wantCover = this.role !== 'push' || this._distToTarget < 8;
    if (wantCover) this._ensureCover();
    else if (this.cover) this._releaseCover();

    if (this.cover) {
      const atCover = this.position.distanceTo(this.cover.position) < 1.0;
      if (!atCover) {
        this._goTo(this.cover.position, TUNING.combatSpeed);
        this.crouching = false;
        this.peeking = false;
      } else {
        this.moveTarget = null;
        this._peekCycle(dt);
      }
    } else {
      // No cover: close the distance, but only if the director says push.
      const d = this._distToTarget;
      const ideal = this.role === 'push' ? 9 : 17;
      if (d > ideal + 4) {
        this._goTo(this.lastKnown, this.role === 'push' ? TUNING.sprintSpeed : TUNING.combatSpeed);
        this.peeking = true;
      } else if (d < ideal - 5) {
        // Back off along the reverse bearing rather than standing in the open.
        _a.subVectors(this.position, this.lastKnown).setY(0).normalize().multiplyScalar(5).add(this.position);
        this._goTo(_a, TUNING.combatSpeed);
        this.peeking = true;
      } else {
        this.moveTarget = null;
        this.peeking = true;
        // Strafe a little so they are not a static target.
        this._strafe(dt);
      }
      this.crouching = false;
    }

    if (this.mag <= 0) this._beginReload();
  }

  _suppressed(dt) {
    this.crouching = !!this.cover;
    this.peeking = false;
    if (this.cover) {
      const atCover = this.position.distanceTo(this.cover.position) < 1.1;
      if (!atCover) this._goTo(this.cover.position, TUNING.sprintSpeed);
      else this.moveTarget = null;
    } else {
      this._ensureCover();
      if (this.cover) this._goTo(this.cover.position, TUNING.sprintSpeed);
      else this.moveTarget = null;
    }
    if (this.suppression <= TUNING.suppressionThreshold * 0.4 && this.stateTime > 0.7) {
      this._setState(this.target ? STATE.COMBAT : STATE.SEARCH);
    }
    void dt;
  }

  _flank(dt) {
    this.crouching = false;
    if (!this.target && this.lastKnownAge > TUNING.memoryTime) {
      this._setState(STATE.SEARCH);
      return;
    }
    if (!this.flankPoint || this.stateTime > 14) {
      this.flankPoint = this.director?.pickFlankPoint(this, this.lastKnown);
      this.stateTime = 0;
      if (!this.flankPoint) {
        this.role = 'suppress';
        this._setState(STATE.COMBAT);
        return;
      }
    }
    const d = this.position.distanceTo(this.flankPoint);
    if (d > 1.6) {
      this._goTo(this.flankPoint, TUNING.sprintSpeed);
      // Only shoot on the move if the shot is genuinely there.
      this.peeking = this.canSee && this._distToTarget < 22;
    } else {
      this.flankPoint = null;
      this.role = 'push';
      this._setState(STATE.COMBAT);
    }
    if (this.mag <= 0) this._beginReload();
    void dt;
  }

  _reloadBehaviour() {
    this.peeking = false;
    this.crouching = !!this.cover && this.position.distanceTo(this.cover.position) < 1.2;
    if (this.cover && !this.crouching) this._goTo(this.cover.position, TUNING.sprintSpeed);
    else this.moveTarget = null;
  }

  _beginReload() {
    if (this.state === STATE.RELOAD) return;
    this.reloadTimer = TUNING.reloadTime;
    this._setState(STATE.RELOAD);
    this.burstLeft = 0;
    // No spatial reload sound exists in the bank — the `reload_*` ids are the
    // player's own non-spatial weapon mech. Silence beats a click in your ear.
  }

  /** Alternate exposed/hidden while holding a cover position. */
  _peekCycle(dt) {
    this.peekTimer -= dt;
    if (this.peekTimer <= 0) {
      this.peeking = !this.peeking;
      this.peekTimer = this.peeking
        ? TUNING.peekTimeMin + this.rng() * (TUNING.peekTimeMax - TUNING.peekTimeMin)
        : TUNING.duckTimeMin + this.rng() * (TUNING.duckTimeMax - TUNING.duckTimeMin);
      // A suppressed soldier stays down longer.
      if (!this.peeking) this.peekTimer *= 1 + this.suppression * 0.6;
    }
    // A cover spot you can never shoot from is worse than none: if we have been
    // exposed for a while and still have no shot, go find a better position.
    if (this.peeking && !this.canSee && this.target) {
      this._blindTimer = (this._blindTimer ?? 0) + dt;
      if (this._blindTimer > 1.8) {
        this._blindTimer = 0;
        this._releaseCover();
        this._coverCooldown = 0;
        return;
      }
    } else this._blindTimer = 0;

    // Low cover means crouching behind it; tall cover means standing beside it.
    const low = this.cover.height < 1.5;
    this.crouching = low && !this.peeking;
    if (!low) {
      // Lean out / step back for tall cover by nudging the stand position.
      const side = this.cover.side ?? 1;
      _a.set(-this.cover.normal.z, 0, this.cover.normal.x).multiplyScalar(side * (this.peeking ? 0.85 : 0));
      _a.add(this.cover.position);
      if (this.position.distanceToSquared(_a) > 0.09) {
        this.desiredSpeed = TUNING.walkSpeed;
        this.moveTarget = _a.clone();
      } else this.moveTarget = null;
    }
  }

  _strafe(dt) {
    this._strafeTimer = (this._strafeTimer ?? 0) - dt;
    if (this._strafeTimer <= 0) {
      this._strafeTimer = 1.2 + this.rng() * 1.8;
      this._strafeDir = this.rng() < 0.5 ? 1 : -1;
    }
    if (!this.target) return;
    _a.subVectors(this.lastKnown, this.position).setY(0).normalize();
    _b.set(-_a.z, 0, _a.x).multiplyScalar(this._strafeDir * 3.5).add(this.position);
    const nav = this.director?.nav;
    if (!nav || nav.isWalkable(_b)) {
      this.desiredSpeed = TUNING.combatSpeed;
      this.moveTarget = _b.clone();
    }
  }

  /* --------------------------------- cover -------------------------------- */

  _ensureCover() {
    if (this.cover) {
      // Abandon cover that no longer protects us.
      const stillGood = this.director.coverProtects(this.cover, this.lastKnown);
      if (stillGood && this.position.distanceTo(this.cover.position) < TUNING.coverSearchRadius) return;
      this._releaseCover();
    }
    if ((this._coverCooldown ?? 0) > 0) return;
    this._coverCooldown = 0.6 + this.rng() * 0.8;
    this.cover = this.director.claimCover(this, this.lastKnown);
  }

  _releaseCover() {
    if (this.cover) this.director.releaseCover(this);
    this.cover = null;
  }

  /* ------------------------------------------------------------------ *
   * Aiming and shooting                                                 *
   * ------------------------------------------------------------------ */

  _updateAim(dt) {
    this._coverCooldown = Math.max(0, (this._coverCooldown ?? 0) - dt);

    const hasTarget = !!this.target && this.lastKnownAge < TUNING.memoryTime;
    if (!hasTarget) {
      this.aimTime = 0;
      // Look where we are going, or where the noise came from.
      const look = this.moveTarget ?? (this.hasSuspicion ? this.suspicion : null);
      if (look) {
        _a.subVectors(look, this.position).setY(0);
        if (_a.lengthSq() > 0.01) {
          _a.normalize();
          this.aimDir.lerp(_a, Math.min(1, dt * 4)).normalize();
        }
      }
      return;
    }

    const muzzle = this.model.getMuzzle(_e);
    const aimPoint = _a.set(this.lastKnown.x, this.lastKnown.y + 0.95, this.lastKnown.z);
    _b.subVectors(aimPoint, muzzle);
    const dist = _b.length();
    if (dist < 0.01) return;
    _b.multiplyScalar(1 / dist);

    if (this.canSee) this.aimTime += dt;
    else this.aimTime = Math.max(0, this.aimTime - dt * 1.6);

    // Error shrinks with sustained aim; grows with range, movement and suppression.
    const settle = clamp(this.aimTime / TUNING.aimSettle, 0, 1);
    const rangeErr = THREE.MathUtils.lerp(TUNING.aimErrorNear, TUNING.aimErrorFar, clamp(dist / 45, 0, 1));
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    let errDeg =
      rangeErr +
      TUNING.aimErrorCold * (1 - settle) * (1 - settle) +
      TUNING.aimErrorMoving * clamp(speed / TUNING.combatSpeed, 0, 1) +
      TUNING.aimErrorSuppressed * clamp(this.suppression, 0, 1.4);
    // Live pacing curve and the chosen skill level both apply: the setting says
    // how good these soldiers are, the curve says how hard they are leaning today.
    errDeg *= (this.director?.difficulty ?? 1) * this.skill.aim;

    // A wandering offset rather than white noise: it reads as a human trying to
    // hold a sight picture instead of a random number generator.
    this.aimErrPhase += dt;
    const t = this.aimErrPhase;
    const ox = (Math.sin(t * 1.7 + this.index) * 0.6 + Math.sin(t * 3.9 + this.index * 2.1) * 0.4) * errDeg * DEG;
    const oy = (Math.cos(t * 1.3 + this.index * 1.7) * 0.6 + Math.cos(t * 4.3 + this.index) * 0.4) * errDeg * DEG;

    // Build the errored direction in the target's local frame.
    _c.set(-_b.z, 0, _b.x).normalize(); // right
    _d.crossVectors(_c, _b).normalize(); // up
    _b.addScaledVector(_c, ox).addScaledVector(_d, oy).normalize();

    const turn = Math.min(1, dt * TUNING.aimTurnRate);
    this.aimDir.lerp(_b, turn).normalize();
  }

  _updateWeapon(dt) {
    this.fireTimer -= dt;
    this.burstGap -= dt;
    if (this.state === STATE.RELOAD || this.state === STATE.DEAD) return;
    if (!this.target) return;
    if (this.reactionTimer > 0) return;
    if (!this.peeking) return;
    if (this.mag <= 0) {
      this._beginReload();
      return;
    }

    // Fire only with a plausible firing solution: either we see them, or we are
    // deliberately suppressing their last known position.
    const suppressing = !this.canSee && this.lastKnownAge < 3.5 && this.role === 'suppress';
    if (!this.canSee && !suppressing) return;
    if (this._distToTarget > TUNING.range) return;

    if (this.burstLeft <= 0) {
      if (this.burstGap > 0) return;
      // Don't shoot through a team-mate.
      if (this.director.friendlyInLine(this)) {
        this.burstGap = 0.35;
        return;
      }
      this.burstLeft =
        TUNING.burstMin + Math.floor(this.rng() * (TUNING.burstMax - TUNING.burstMin + 1));
      if (suppressing) this.burstLeft += 2;
    }

    // Blinded or shocked: the burst bookkeeping above still runs, so they resume on
    // the same cadence rather than starting a fresh burst the instant they recover.
    if (this.incapacitated) return;

    if (this.fireTimer <= 0) {
      this._shoot();
      this.fireTimer = TUNING.fireInterval;
      this.burstLeft--;
      this.mag--;
      if (this.burstLeft <= 0 || this.mag <= 0) {
        const base = suppressing ? TUNING.suppressBurstGap : TUNING.burstGapMin;
        this.burstGap = base + this.rng() * (TUNING.burstGapMax - base);
        if (this.suppression > 0.4) this.burstGap *= 1.4;
      }
    }
  }

  _shoot() {
    const origin = this.model.getMuzzle(_e).clone();
    const dir = _a.copy(this.aimDir);
    // Per-shot spread on top of the aim error: burst climb, human tremor.
    const s = TUNING.perShotSpread * DEG * (1 + (TUNING.burstMax - this.burstLeft) * 0.14);
    dir.x += (this.rng() - 0.5) * 2 * s;
    dir.y += (this.rng() - 0.5) * 2 * s;
    dir.z += (this.rng() - 0.5) * 2 * s;
    dir.normalize();

    this.model.fireFlash();
    this.director.onEnemyFired(this, origin, dir);

    this.events.emit('audio:play', {
      id: 'enemy_fire',
      position: origin,
      volume: 0.85,
      pitch: 0.95 + this.rng() * 0.1,
    });

    const hit = this.physics.raycast(origin, dir, TUNING.range, MASK.ALL, this);
    if (!hit.hit) return;

    if (hit.actor) {
      /**
       * Friendly fire lands but does no damage — soldiers do not gun each other
       * down, yet a body in the way should still stop the round.
       *
       * Used to read `if (hit.actor === this.ctx.player)`, which was both the
       * friendly-fire rule and the target list in one literal. The match knows who
       * is on whose side now, so a round that lands on a squadmate is refused there
       * instead — and the day a second human joins, this line already does the right
       * thing without being touched.
       */
      const res = this.ctx.match?.requestDamage({
        target: hit.actor,
        source: this,
        amount: TUNING.damage,
        hitbox: hit.hitbox,
        point: hit.point,
        normal: hit.normal,
        dir: dir.clone(),
        means: MEANS.BULLET,
      });
      if (res && !res.blocked) {
        this.events.emit('hit:actor', {
          actor: hit.actor,
          point: hit.point,
          normal: hit.normal,
          damage: res.amount,
          headshot: res.headshot,
          dir: dir.clone(),
          hitbox: hit.hitbox,
          source: this,
        });
      }
    } else {
      this.events.emit('hit:surface', {
        point: hit.point,
        normal: hit.normal,
        material: hit.materialId,
        entity: null,
        dir: dir.clone(),
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Movement                                                            *
   * ------------------------------------------------------------------ */

  _goTo(target, speed) {
    this.desiredSpeed = speed;
    if (!this.moveTarget) this.moveTarget = new THREE.Vector3();
    this.moveTarget.copy(target);
  }

  _move(dt) {
    // --- path maintenance ---
    this.repathTimer -= dt;
    if (this.moveTarget) {
      const needs =
        this.path.length === 0 ||
        this.repathTimer <= 0 ||
        this.pathTarget.distanceToSquared(this.moveTarget) > TUNING.repathMoveThreshold ** 2;
      if (needs && !this.pathPending) {
        this.repathTimer = TUNING.repathInterval + this.rng() * 0.3;
        this.pathTarget.copy(this.moveTarget);
        this.director.requestPath(this, this.moveTarget);
      }
    } else {
      this.path.length = 0;
      this.pathIndex = 0;
    }

    // --- desired direction ---
    const wish = this._wish.set(0, 0, 0);
    let speed = 0;
    if (this.moveTarget) {
      let wp = null;
      if (this.pathIndex < this.path.length) {
        wp = this.path[this.pathIndex];
        const d2 = (wp.x - this.position.x) ** 2 + (wp.z - this.position.z) ** 2;
        if (d2 < 0.55 * 0.55) {
          this.pathIndex++;
          wp = this.pathIndex < this.path.length ? this.path[this.pathIndex] : null;
        }
      }
      const goal = wp ?? this.moveTarget;
      wish.set(goal.x - this.position.x, 0, goal.z - this.position.z);
      const dist = wish.length();
      if (dist > 0.001) wish.multiplyScalar(1 / dist);
      speed = this.desiredSpeed ?? TUNING.walkSpeed;
      if (this.crouching) speed = Math.min(speed, TUNING.crouchSpeed);
      // Concussed or shocked. Multiplied rather than clamped so it scales whatever
      // gait they were using — a sprinting flank is slowed as much as a walk.
      if (this.status.slowT > 0) speed *= this.status.slowMul;
      // Ease into the last metre so they stop cleanly instead of oscillating.
      const finalDist = Math.hypot(
        this.moveTarget.x - this.position.x,
        this.moveTarget.z - this.position.z
      );
      if (finalDist < 1.1) speed *= clamp(finalDist / 1.1, 0.15, 1);
      if (finalDist < 0.34 && this.pathIndex >= this.path.length) {
        this.moveTarget = null;
        speed = 0;
      }
    }

    if (speed > 0.01 && wish.lengthSq() > 0.001) {
      const nav = this.director.nav;
      const doWhiskers = (this.ctx.engine.frame + this.index) % 4 === 0;
      nav.steer(this, wish, this.director.enemies, this._steerOut, doWhiskers);
      wish.copy(this._steerOut);
    }

    // --- integrate ---
    const target = _a.set(wish.x * speed, 0, wish.z * speed);
    const accel = TUNING.accel * dt;
    this.velocity.x += clamp(target.x - this.velocity.x, -accel, accel);
    this.velocity.z += clamp(target.z - this.velocity.z, -accel, accel);
    if (speed < 0.01) {
      const f = Math.max(0, 1 - TUNING.friction * dt);
      this.velocity.x *= f;
      this.velocity.z *= f;
    }

    this.velocity.y -= TUNING.gravity * dt;
    if (this.velocity.y < -55) this.velocity.y = -55;

    // Stance height, damped so the capsule never pops.
    const wantH = this.crouching ? TUNING.crouchHeight : TUNING.standHeight;
    this.height = THREE.MathUtils.damp(this.height, wantH, 10, dt);
    this.crouchAmount = clamp(
      (TUNING.standHeight - this.height) / (TUNING.standHeight - TUNING.crouchHeight),
      0,
      1
    );

    _delta.copy(this.velocity).multiplyScalar(dt);
    const res = this.physics.sweepCapsule(this.position, TUNING.radius, this.height, _delta);
    this.position.copy(res.position);
    this.grounded = res.grounded;
    if (res.grounded) {
      this._footMat = res.groundMaterial;
      if (this.velocity.y < 0) this.velocity.y = 0;
    }
    if (res.hitWall) {
      if (res.wallNormal.x !== 0) this.velocity.x = 0;
      if (res.wallNormal.z !== 0) this.velocity.z = 0;
    }

    // --- unstick ---
    const moved = this.position.distanceToSquared(this._lastPos);
    if (this.moveTarget && speed > 0.5 && moved < (0.15 * dt) ** 2) {
      this.stuckTimer += dt;
      if (this.stuckTimer > 0.9) {
        this.stuckTimer = 0;
        this.path.length = 0;
        this.pathIndex = 0;
        this.repathTimer = 0;
        // Sidestep: rotating the wish vector breaks symmetric deadlocks.
        this.velocity.x += (this.rng() - 0.5) * 4;
        this.velocity.z += (this.rng() - 0.5) * 4;
      }
    } else {
      this.stuckTimer = Math.max(0, this.stuckTimer - dt);
    }
    this._lastPos.copy(this.position);

    // --- facing ---
    let faceDir = null;
    if (this.target && this.lastKnownAge < TUNING.memoryTime) {
      faceDir = _b.set(this.aimDir.x, 0, this.aimDir.z);
    } else if (speed > 0.3) {
      faceDir = _b.set(this.velocity.x, 0, this.velocity.z);
    }
    if (faceDir && faceDir.lengthSq() > 0.0004) {
      faceDir.normalize();
      const want = Math.atan2(-faceDir.x, -faceDir.z);
      let diff = want - this.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const rate = TUNING.turnRate * (this.target ? 1 : 0.6);
      this.yaw += clamp(diff, -rate * dt, rate * dt);
    }
  }

  _faceIdle(dt) {
    this._idleLookTimer = (this._idleLookTimer ?? 0) - dt;
    if (this._idleLookTimer <= 0) {
      this._idleLookTimer = 3 + this.rng() * 4;
      this._idleLookYaw = this.yaw + (this.rng() - 0.5) * 2.4;
    }
    if (this._idleLookYaw !== undefined) {
      let diff = this._idleLookYaw - this.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.yaw += clamp(diff, -1.2 * dt, 1.2 * dt);
    }
  }

  _faceTowards(pos, dt, rate = 4) {
    _a.set(pos.x - this.position.x, 0, pos.z - this.position.z);
    if (_a.lengthSq() < 0.0004) return;
    _a.normalize();
    const want = Math.atan2(-_a.x, -_a.z);
    let diff = want - this.yaw;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.yaw += clamp(diff, -rate * dt, rate * dt);
  }

  _snapToGround() {
    const gy = this.physics.groundHeight(this.position.x, this.position.z, this.position.y + 3, 12);
    if (gy > this.position.y - 12) this.position.y = gy + 0.02;
  }

  /* ------------------------------------------------------------------ *
   * Presentation                                                        *
   * ------------------------------------------------------------------ */

  _updateModel(dt) {
    const m = this.model;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const engaged = !!this.target || this.state === STATE.ALERT || this.state === STATE.SEARCH ||
      this.state === STATE.RELOAD || this.state === STATE.SUPPRESSED;

    // Rifle is slung only when genuinely idle and stationary.
    const hold = engaged || speed > 0.4 || this.state !== STATE.IDLE ? 1 : 0;
    // Shouldered only when actually able to shoot at something.
    const aiming =
      this.target && this.peeking && this.reactionTimer <= 0 && this.state !== STATE.RELOAD ? 1 : 0;

    // Velocity in model space, so the legs cycle backwards when backpedalling and
    // the hips abduct when sidestepping instead of scissoring the wrong way.
    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);
    _c.set(this.velocity.x * cy - this.velocity.z * sy, 0, this.velocity.x * sy + this.velocity.z * cy);

    m.update(dt, {
      position: this.position,
      yaw: this.yaw,
      speed,
      moveLocal: _c,
      // Drive the visual stance from the capsule, not the intent, so the head and
      // hitboxes can never disagree with the collision shape.
      crouch: this.crouchAmount,
      hold,
      aim: aiming,
      aimDir: this.aimDir,
    });

    if (m.footEvent) {
      // `enemy_step` is the spatial, world-bus variant. The `step_*` ids are the
      // player's own non-spatial footsteps and would play inside your head.
      this.events.emit('audio:play', {
        id: 'enemy_step',
        position: this.position.clone(),
        volume: clamp(0.35 + speed * 0.12, 0.3, 1.15),
        pitch: 0.86 + this.rng() * 0.18,
      });
    }
  }

  /** Hitboxes track the animated bones so shots land where the body actually is. */
  _syncHitboxes() {
    for (const hb of this.hitboxes) hb.worldMatrix.copy(hb.bone.matrixWorld);
    this.matrixWorld.copy(this.model.root.matrixWorld);
  }

  /* ------------------------------------------------------------------ *
   * Damage                                                              *
   * ------------------------------------------------------------------ */

  /* --------------------------- status effects ----------------------------- */

  /**
   * Apply a tactical-equipment effect.
   *
   * Three of them, because three is what the throwables can actually do to a mind:
   * take its sight, take its legs, or take its trigger. Each is a timer and nothing
   * more — statuses do not stack in strength, they take the longer of the two
   * remaining times and the stronger of the two effects, so two flashes are one long
   * flash rather than a compounding blackout nobody recovers from.
   *
   * @param kind      'blind' | 'slow' | 'inhibit'
   * @param seconds   how long
   * @param strength  for 'slow', the movement/aim multiplier at full effect
   */
  applyStatus(kind, seconds, strength = 1) {
    if (this.dead || !(seconds > 0)) return;
    const s = this.status;
    if (kind === 'blind') {
      s.blindT = Math.max(s.blindT, seconds);
      // Being flashed is not the same as being unaware: they know something happened
      // and where roughly it came from, they simply cannot act on it yet.
      this.suppression = Math.max(this.suppression, 1.2);
    } else if (kind === 'slow') {
      s.slowT = Math.max(s.slowT, seconds);
      s.slowMul = Math.min(s.slowMul, Math.max(0.05, strength));
    } else if (kind === 'inhibit') {
      s.inhibitT = Math.max(s.inhibitT, seconds);
    }
  }

  _tickStatus(dt) {
    const s = this.status;
    if (s.blindT > 0) s.blindT -= dt;
    if (s.inhibitT > 0) s.inhibitT -= dt;
    if (s.slowT > 0) {
      s.slowT -= dt;
      if (s.slowT <= 0) s.slowMul = 1;
    }
  }

  /** True while anything is stopping them from bringing the weapon to bear. */
  get incapacitated() {
    return this.status.blindT > 0 || this.status.inhibitT > 0;
  }

  /**
   * The match has ruled that we are hit. Write the number and react to it.
   *
   * Everything below the health line is presentation and AI: the flinch, the
   * suppression, noticing where the shot came from. None of it decides anything the
   * other machine in a match would have to agree with, which is exactly why it can
   * stay here while the arithmetic moved out.
   */
  applyDamage(res) {
    this.health = res.after;
    if (res.fatal) return; // `applyDeath` is already on its way; do not flinch first.

    const hb = res.hitbox;
    const dmg = res.amount;
    const dir = res.dir ?? _a.set(0, 0, 1);

    this.model.flinch(dir, hb?.boneName ?? 'chest', clamp(dmg / 40, 0.25, 1.1));
    this.suppression += 0.8;
    this.awareness = 1;
    /**
     * Being shot tells you roughly where from, even if you cannot see the shooter.
     *
     * This used to say "the player", flatly, because the player was the only thing
     * that could shoot. Now the request names its attacker, so a bot shot by another
     * bot turns on the right one. Falling back to the player keeps single-player
     * identical for the sources that still have no owner (the kill plane, a barrel).
     */
    const attacker = res.source?.actor ?? this.ctx.player;
    if (attacker && attacker !== this) {
      this.lastKnown.copy(attacker.position);
      this.lastKnownAge = 0;
      if (!this.target) {
        this.target = attacker;
        this.reactionTimer = TUNING.reactionMin * 0.6 * this.skill.reaction;
        this.director?.onSpotted(this);
      }
    }
    if (this.state === STATE.IDLE || this.state === STATE.ALERT) this._setState(STATE.COMBAT);

    this.events.emit('audio:play', {
      id: 'enemy_pain',
      position: this.position.clone(),
      volume: 0.7,
      pitch: 0.9 + this.rng() * 0.25,
    });
  }

  /**
   * Go down. Ragdoll, sound, leave the physics world so the corpse is walk-through.
   *
   * Deliberately does not emit `actor:death` any more. The authority does that, once,
   * after it has attributed the kill — which is what lets a listener finally tell
   * "someone died" apart from "I killed someone".
   */
  applyDeath(res) {
    if (this.dead) return;
    this.dead = true;
    this.health = 0;
    this._setState(STATE.DEAD);
    this._releaseCover();
    this.physics.removeActor(this.id);

    _b.copy(this.velocity).multiplyScalar(0.4);
    this.model.startRagdoll(
      res?.dir ?? _a.set(0, 0, 1),
      res?.headshot ? 1.8 : 1.0,
      res?.hitbox?.tag ?? 'chest',
      _b,
      this.position.y
    );

    this.events.emit('audio:play', {
      id: 'enemy_death',
      position: this.position.clone(),
      volume: 0.9,
      pitch: 0.92 + this.rng() * 0.16,
    });
  }

  dispose() {
    this.physics.removeActor(this.id);
    this.ctx.match?.unregister(this.id);
    this.ctx.scene.remove(this.model.root);
    this.model.dispose();
  }
}
